import {updateCloudinarySize} from '../cloudinary'
import type {VideoSource} from '../cloudinary/video'
import type {CloudinaryVideoOrientation, ProductImages, ProductVideos} from '../../types/product'

export interface MediaItem {
    type: 'image' | 'video'
    url: string
    alt?: string
    isHorizontal?: boolean
    urlPdpZoom?: string
    urlSwatch?: string
    // New responsive video contract (c_cloudinaryVideo). When present, the atom Video renders
    // one <source> per entry; `url` is kept as the universal (H.264) fallback so downstream
    // zoom/track/key logic keeps working. Absent for legacy single-URL video items.
    sources?: VideoSource[]
    poster?: string
}

interface ComputeMediaItemsOptions {
    images?: ProductImages
    videos?: ProductVideos
    isDesktop?: boolean
    isTablet?: boolean
    type?: 'grid3' | 'grid2' | 'pdp' | 'full' | 'horizontal' | 'wishlist' | 'shoptheimage' | 'shopbylook'
}

/**
 * Computes the ordered list of media items for a product variation.
 * Shared between ProductMediaCarousel and ProductDetails so both use
 * identical ordering logic and URL optimization.
 */
export function computeMediaItems({images, videos, isDesktop, isTablet, type}: ComputeMediaItemsOptions): MediaItem[] {
    const items: MediaItem[] = []
    const deviceType = isDesktop ? 'desktop' : isTablet ? 'tablet' : 'mobile'
    const size = type ? (TILE_ASSET_SIZE as any)[deviceType][type] : isDesktop ? window.innerWidth / 2 : window.innerWidth
    images?.large
        ?.filter((img) => Boolean(img?.url))
        .forEach((img) =>
            items.push({
                type: 'image',
                url: updateCloudinarySize(img.url as string, size),
                urlPdpZoom: img.url,
                urlSwatch: updateCloudinarySize(img.url as string, 112),
                alt: img.alt,
                isHorizontal: false,
            }),
        )

    if (images?.horizontal?.url && type !== 'shopbylook') {
        items.push({
            type: 'image',
            url: updateCloudinarySize(images.horizontal.url, size),
            alt: images.horizontal.alt,
            isHorizontal: true,
            urlPdpZoom: images.horizontal.url,
            urlSwatch: updateCloudinarySize(images.horizontal.url, 112),
        })
    }
    const hasHorizontalImage = Boolean(images?.horizontal?.url)

    // Build a video MediaItem, preferring the new c_cloudinaryVideo contract (pre-built responsive
    // sources + poster) and falling back to the legacy single CLDvideo*URL. The `type !== 'shopbylook'`
    // gate is preserved. Returns undefined when neither the new nor the legacy source exists.
    const buildVideoItem = (orientation: 'vertical' | 'horizontal'): MediaItem | undefined => {
        if (type === 'shopbylook') return undefined
        const isHorizontal = orientation === 'horizontal'

        const cloudinary: CloudinaryVideoOrientation | null | undefined = videos?.c_cloudinaryVideo?.[orientation]
        if (cloudinary?.sources?.length) {
            // Codec order is baked by the backend (AV1→VP9→HEVC→H264); pass through verbatim.
            // `url` = last source (universal H.264 fallback) so zoom/track/key logic keeps working.
            const fallbackUrl = cloudinary.sources[cloudinary.sources.length - 1]?.src ?? ''
            return {type: 'video', url: fallbackUrl, isHorizontal, sources: cloudinary.sources, poster: cloudinary.poster}
        }

        // Legacy fallback: gated on the existing hasVerticalVideo/hasHorizontalVideo flags.
        const legacyUrl = isHorizontal
            ? videos?.hasHorizontalVideo
                ? videos.videoH?.CLDvideoHURL
                : undefined
            : videos?.hasVerticalVideo
              ? videos.videoV?.CLDvideoVURL
              : undefined
        if (legacyUrl) return {type: 'video', url: legacyUrl, isHorizontal}

        return undefined
    }

    const videoVItem = buildVideoItem('vertical')
    const videoHItem = buildVideoItem('horizontal')

    // Ordering preserved exactly: vertical video is unshifted to the front when a horizontal image
    // exists, otherwise pushed; the horizontal video is always pushed last.
    if (hasHorizontalImage && videoVItem) items.unshift(videoVItem)
    else if (videoVItem) items.push(videoVItem)
    if (videoHItem) items.push(videoHItem)

    return items
}

export const TILE_ASSET_SIZE = {
    mobile: {
        grid3: 300,
        grid2: 500,
        full: 500,
        horizontal: 500,
        wishlist: 500,
        shoptheimage: 300,
        pdp: 500,
        carousel: 500,
        shopbylook: 500,
    },
    tablet: {
        grid3: 320,
        grid2: 490,
        full: 600,
        horizontal: 780,
        wishlist: 490,
        shoptheimage: 320,
        pdp: 1000,
        carousel: 600,
        shopbylook: 1000,
    },
    desktop: {
        grid3: 900,
        grid2: 1000,
        full: 1230,
        horizontal: 1600,
        wishlist: 900,
        shoptheimage: 900,
        pdp: 1400,
        carousel: 900,
        shopbylook: 1400,
    },
}
